import { useState } from "react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { OverviewSettingRow } from "@/components/agent/overview/OverviewSettingRow";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";

const SILENCE_END_CALL_OFF = -1;
const DEFAULT_SILENCE_END_CALL_SECONDS = 300;

interface ConversationBehaviorCardProps {
  agent: Agent;
  brand?: string;
}

export const ConversationBehaviorCard = ({ agent, brand = "#4E9CFF" }: ConversationBehaviorCardProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const config = agent.settings?.conversation_config;
  const conv = config?.conversation;
  const turn = config?.turn;

  const [maxDuration, setMaxDuration] = useState<number | "">(conv?.max_duration_seconds ?? "");
  const [textOnly, setTextOnly] = useState(conv?.text_only ?? false);
  const [turnMode, setTurnMode] = useState(turn?.mode ?? "turn");
  const [turnTimeout, setTurnTimeout] = useState<number | "">(turn?.turn_timeout ?? "");
  const [silenceTimeout, setSilenceTimeout] = useState<number | "">(turn?.silence_end_call_timeout ?? "");

  const updateAgent = useUpdateAgent();

  const handleEdit = () => {
    setMaxDuration(conv?.max_duration_seconds ?? "");
    setTextOnly(conv?.text_only ?? false);
    setTurnMode(turn?.mode ?? "turn");
    setTurnTimeout(turn?.turn_timeout ?? "");
    setSilenceTimeout(turn?.silence_end_call_timeout ?? "");
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSilenceToggle = (enabled: boolean) => {
    if (!enabled) {
      setSilenceTimeout(SILENCE_END_CALL_OFF);
      return;
    }
    const saved = turn?.silence_end_call_timeout;
    setSilenceTimeout(saved != null && saved !== SILENCE_END_CALL_OFF ? saved : DEFAULT_SILENCE_END_CALL_SECONDS);
  };

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        settings: {
          ...agent.settings,
          conversation_config: {
            ...config,
            conversation: {
              ...conv,
              max_duration_seconds: maxDuration !== "" ? Number(maxDuration) : null,
              text_only: textOnly,
            },
            turn: {
              ...turn,
              mode: turnMode,
              turn_timeout: turnTimeout !== "" ? Number(turnTimeout) : null,
              silence_end_call_timeout: silenceTimeout !== "" ? Number(silenceTimeout) : null,
            },
          },
        },
      },
    });
    toast.success("Conversation settings saved");
    setIsEditing(false);
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes === 0) return `${secs}s`;
    if (secs === 0) return `${minutes}m`;
    return `${minutes}m ${secs}s`;
  };

  return (
    <OverviewSectionCard
      title="Conversation Behavior"
      subtitle="Duration limits and turn-taking flow"
      genieIcon="conversation"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Duration (s)</label>
              <input
                type="number"
                value={maxDuration}
                onChange={(e) => setMaxDuration(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. 600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Turn Mode</label>
              <select
                value={turnMode}
                onChange={(e) => setTurnMode(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="turn">Turn Based</option>
                <option value="silence">Silence Based</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Turn Timeout (s)</label>
              <input
                type="number"
                value={turnTimeout}
                onChange={(e) => setTurnTimeout(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Silence End Call (s)</label>
                <Switch
                  aria-label="Silence End Call"
                  checked={silenceTimeout !== SILENCE_END_CALL_OFF}
                  onCheckedChange={handleSilenceToggle}
                />
              </div>
              {silenceTimeout === SILENCE_END_CALL_OFF ? (
                <input
                  type="text"
                  value="Off"
                  disabled
                  className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                />
              ) : (
                <input
                  type="number"
                  min={0}
                  value={silenceTimeout}
                  onChange={(e) => setSilenceTimeout(e.target.value === "" ? "" : Number(e.target.value))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              )}
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={textOnly}
              onChange={(e) => setTextOnly(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-sm font-medium text-gray-700">Text Only Mode</span>
          </label>
        </div>
      ) : (
        <div className="space-y-4">
          {conv?.max_duration_seconds != null && (
            <OverviewSettingRow
              label="Max Duration"
              value={<span className="font-medium">{formatDuration(conv.max_duration_seconds)}</span>}
            />
          )}
          <OverviewSettingRow
            label="Turn Mode"
            value={<Badge variant="outline">{turn?.mode === "silence" ? "Silence Based" : "Turn Based"}</Badge>}
          />
          {turn?.turn_timeout != null && (
            <OverviewSettingRow
              label="Turn Timeout"
              value={<span className="font-medium">{turn.turn_timeout}s</span>}
            />
          )}
          {turn?.silence_end_call_timeout != null && (
            <OverviewSettingRow
              label="Silence End Call"
              value={
                <span className="font-medium">
                  {turn.silence_end_call_timeout === SILENCE_END_CALL_OFF ? "Off" : `${turn.silence_end_call_timeout}s`}
                </span>
              }
            />
          )}
          <OverviewSettingRow
            label="Text Only"
            value={<Badge variant={conv?.text_only ? "default" : "secondary"}>{conv?.text_only ? "Yes" : "No"}</Badge>}
          />
        </div>
      )}
    </OverviewSectionCard>
  );
};
